import {
    SalesCampaignDiscountModelOptionalId,
    SalesCampaignOverviewRow,
} from 'src/domain/table/sales-campaign-overview.row';
import {
    CreateSalesCampaignDiscountInput,
    ExtendedSalesCampaignDiscountType,
    UpdateSalesCampaignDiscountInput,
} from 'src/infrastructure/rest-api/api-types';
import { parseNumber } from 'src/utils/formatNumber';
import { calculateDiscountedSalesPrice } from 'src/utils/gross-margin/calculateGrossMargin';
import { calculatePercentageDifference, safeRound } from 'src/utils/math';

type DiscountValues = Pick<SalesCampaignDiscountModelOptionalId, 'valueX' | 'valueY' | 'valueZ'>;
type DiscountCellPrimitive = number | string | null;

export interface SalesCampaignDiscountCellValue {
    x?: DiscountCellPrimitive;
    y?: DiscountCellPrimitive;
    z?: DiscountCellPrimitive;
    discountValue?: number;
    discountType?: ExtendedSalesCampaignDiscountType;
}

// The API stores Percentage in valueX; the frontend stores it in valueY so all calculations use one shape.
export function mapSalesCampaignDiscountFromApi(
    discount: SalesCampaignDiscountModelOptionalId
): SalesCampaignDiscountModelOptionalId {
    if (discount.discountType !== 'PERCENTAGE') {
        return { ...discount };
    }

    return {
        ...discount,
        valueX: 1,
        valueY: discount.valueX,
    };
}

export function mapSalesCampaignDiscountToInput(
    discount: SalesCampaignDiscountModelOptionalId
): CreateSalesCampaignDiscountInput | UpdateSalesCampaignDiscountInput {
    const isPercentage = discount.discountType === 'PERCENTAGE';

    return {
        discountType: discount.discountType === 'FIXED_PRICE' ? 'X_FOR_FIXED_PRICE' : discount.discountType,
        salesCampaignDiscountId: discount.id,
        salesCampaignPhaseId: discount.salesCampaignPhaseId,
        styleOptionId: discount.styleOptionId,
        valueX: isPercentage ? Number(discount.valueY) : discount.valueX,
        valueY: isPercentage ? null : discount.valueY,
        valueZ: discount.valueZ,
        comment: discount.comment ?? '',
    };
}

export function parsePercentageDiscountInput(value: unknown): number | undefined {
    if (typeof value !== 'string') return;

    const match = /^\s*(-?\d+(?:[.,]\d+)?)\s*%\s*$/.exec(value);
    return match ? parseNumber(match[1]) : undefined;
}

export function parseSalesCampaignDiscountCellInput(value: string): SalesCampaignDiscountCellValue {
    if (!value) {
        return {
            x: 1,
            y: undefined,
            discountType: 'FIXED_PRICE',
        };
    }

    const percentage = parsePercentageDiscountInput(value);
    if (typeof percentage === 'number') {
        return {
            x: 1,
            y: value,
            z: null,
            discountValue: percentage,
            discountType: 'PERCENTAGE',
        };
    }

    const buyXMatch = /^Buy\s+(\d+(?:[.,]\d+)?),\s*Get\s+(\d+(?:[.,]\d+)?)%\s*Off\s+(\d+(?:[.,]\d+)?)$/i.exec(value);
    if (buyXMatch) {
        return {
            x: parseNumber(buyXMatch[1]),
            y: parseNumber(buyXMatch[2]),
            z: parseNumber(buyXMatch[3]),
            discountType: 'BUY_X_GET_Y_PERCENT_OFF_Z',
        };
    }

    const [x, y] = value.split('for').map((part) => part.trim());
    if (y?.includes(',')) {
        return {
            x: parseNumber(x),
            y: parseNumber(y),
            discountType: 'X_FOR_FIXED_PRICE',
        };
    }
    if (y) {
        return {
            x: parseNumber(x),
            y: parseNumber(y),
            discountType: 'X_FOR_Y',
        };
    }

    return {
        x: 1,
        y: parseNumber(value),
        discountType: 'FIXED_PRICE',
    };
}

function parseOptionalDiscountValue(value: DiscountCellPrimitive | undefined, fallback: number): number;
function parseOptionalDiscountValue(value: DiscountCellPrimitive | undefined, fallback: null): number | null;
function parseOptionalDiscountValue(value: DiscountCellPrimitive | undefined, fallback: number | null) {
    return value !== undefined && value !== null && `${value}` !== '' ? parseNumber(value) : fallback;
}

export function applySalesCampaignDiscountCellValue(
    currentData: SalesCampaignOverviewRow,
    value: SalesCampaignDiscountCellValue | null | undefined
): SalesCampaignDiscountModelOptionalId {
    const cellValue = value ?? { x: 1, discountType: 'FIXED_PRICE' };
    const percentage =
        typeof cellValue.discountValue === 'number'
            ? cellValue.discountValue
            : parsePercentageDiscountInput(cellValue.y);

    if (typeof percentage === 'number') {
        return {
            ...currentData.discount,
            discountType: 'PERCENTAGE',
            valueX: 1,
            valueY: percentage,
            valueZ: null,
        };
    }

    const incomingDiscountType = cellValue.discountType ?? currentData.discount.discountType;
    const discountType =
        incomingDiscountType === 'FIXED_PRICE' || incomingDiscountType === 'PERCENTAGE'
            ? 'FIXED_PRICE'
            : incomingDiscountType;

    return {
        ...currentData.discount,
        discountType,
        valueX: discountType === 'FIXED_PRICE' ? 1 : parseOptionalDiscountValue(cellValue.x, 1),
        valueY: parseOptionalDiscountValue(cellValue.y, null),
        valueZ: discountType === 'FIXED_PRICE' ? null : parseOptionalDiscountValue(cellValue.z, null),
    };
}

function percentageValues(data: SalesCampaignOverviewRow, discountedSalesPrice?: number): DiscountValues {
    const percentage = calculatePercentageDifference(discountedSalesPrice, data.recommendedRetailPriceLcy);
    return {
        valueX: 1,
        valueY: typeof percentage === 'number' ? safeRound(percentage) : null,
        valueZ: null,
    };
}

function fixedPriceValues(
    data: SalesCampaignOverviewRow,
    cameFromPercentage: boolean,
    discountedSalesPrice?: number
): DiscountValues {
    return {
        valueX: 1,
        valueY:
            cameFromPercentage && typeof discountedSalesPrice === 'number'
                ? safeRound(discountedSalesPrice)
                : data.discount.valueY,
        valueZ: null,
    };
}

function buyXGetYPercentOffZValues(data: SalesCampaignOverviewRow): DiscountValues {
    return {
        valueX: typeof data.discount.valueX === 'number' && data.discount.valueX > 1 ? data.discount.valueX : 2,
        valueY:
            typeof data.discount.valueY === 'number' && data.discount.valueY > 0 && data.discount.valueY < 100
                ? data.discount.valueY
                : 50,
        valueZ: typeof data.discount.valueZ === 'number' && data.discount.valueZ > 0 ? data.discount.valueZ : 1,
    };
}

function xForFixedPriceValues(
    data: SalesCampaignOverviewRow,
    cameFromPercentage: boolean,
    discountedSalesPrice?: number
): DiscountValues {
    const valueX = typeof data.discount.valueX === 'number' && data.discount.valueX > 0 ? data.discount.valueX : 2;
    return {
        valueX,
        valueY:
            cameFromPercentage && typeof discountedSalesPrice === 'number'
                ? safeRound(discountedSalesPrice * valueX)
                : data.discount.valueY,
        valueZ: null,
    };
}

function xForYValues(data: SalesCampaignOverviewRow): DiscountValues {
    return {
        valueX: data.discount.valueX,
        valueY: typeof data.discount.valueY === 'number' ? Math.floor(data.discount.valueY) : data.discount.valueY,
        valueZ: null,
    };
}

export function changeSalesCampaignDiscountType(
    data: SalesCampaignOverviewRow,
    discountType: ExtendedSalesCampaignDiscountType
): SalesCampaignDiscountModelOptionalId {
    const discountedSalesPrice = calculateDiscountedSalesPrice(data, data.discount);
    const cameFromPercentage = data.discount.discountType === 'PERCENTAGE';

    const valuesByType: Record<ExtendedSalesCampaignDiscountType, () => DiscountValues> = {
        PERCENTAGE: () => percentageValues(data, discountedSalesPrice),
        FIXED_PRICE: () => fixedPriceValues(data, cameFromPercentage, discountedSalesPrice),
        BUY_X_GET_Y_PERCENT_OFF_Z: () => buyXGetYPercentOffZValues(data),
        X_FOR_FIXED_PRICE: () => xForFixedPriceValues(data, cameFromPercentage, discountedSalesPrice),
        X_FOR_Y: () => xForYValues(data),
    };

    return {
        ...data.discount,
        ...valuesByType[discountType](),
        discountType,
    };
}
